import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setClassNames } from 'ddc-classnames-js';
import { usePrefs, useLabels } from 'wsm-common-data';
import { Error } from '../components/Error';
import { Reviews } from '../components/Reviews';
import { Filters } from '../components/Filters';
import { Sort } from '../components/Sort';
import { Pagination } from '../components/Pagination';
import setReviews from '../actions/setReviews';
import setPageInfo from '../actions/setPageInfo';
import { sortReviews } from '../utils/sorting';
import { getAvailableFilters } from '../utils/getAvailableFilters';

function usePrevious(value) {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
}

const Widget = () => {
	const {
		reviews,
		pageInfo,
		filters,
		sort: { sortType, sortFunction },
		dealerName
	} = useSelector((state) => state);
	const prefs = usePrefs();
	const labels = useLabels();
	const pageSize = parseInt(prefs.pageSize, 10);
	const prevFilters = usePrevious(filters);
	const prevSortType = usePrevious(sortType);
	const [filteredAndSortedReviews, setFilteredAndSortedReviews] =
		useState(reviews);
	const availableFilters = getAvailableFilters(reviews);
	const dispatch = useDispatch();

	useEffect(() => {
		let updatedReviews = filteredAndSortedReviews;
		if (prevFilters !== filters) {
			updatedReviews = reviews.filter(({ review }) => {
				// return the review only if the review matches the filtered value or if the filter is in its default state
				return (
					(filters.department === 'department' ||
						filters.department === review.department._text) &&
					(filters.state === 'state' ||
						filters.state === review.author._attributes.state) &&
					(filters.make === 'make' ||
						filters.make === review.vehicle._attributes.make)
				);
			});
		}
		if (prevSortType !== sortType) {
			dispatch(setReviews(sortReviews(reviews, sortFunction)));
			updatedReviews = sortReviews(updatedReviews, sortFunction);
		}
		setFilteredAndSortedReviews(updatedReviews);

		dispatch(
			setPageInfo({
				...pageInfo,
				currentPage: 0,
				pageCount: Math.ceil(updatedReviews.length / pageSize)
			})
		);
	}, [filters, sortType]);

	const filterSortContainerClasses = [
		'filter-sort-container',
		'd-sm-flex',
		'justify-content-between',
		'px-8',
		'px-sm-0'
	];

	const pagedReviews = Array.from(
		{ length: Math.ceil(filteredAndSortedReviews.length / pageSize) },
		(v, i) => {
			return filteredAndSortedReviews.slice(
				i * pageSize,
				i * pageSize + pageSize
			);
		}
	);

	if (!reviews || !reviews.length) {
		return <Error message={labels.get('NO_REVIEWS_AVAILABLE')} />;
	}
	const { currentPage } = pageInfo;
	const errorOrReviews =
		!pagedReviews || !pagedReviews.length ? (
			<Error message={labels.get('NO_MATCHING_REVIEWS_AVAILABLE')} />
		) : (
			<React.Fragment>
				<Reviews
					dealerName={dealerName}
					reviews={pagedReviews[currentPage]}
				/>
				<Pagination />
			</React.Fragment>
		);

	return (
		<div className="rdc-reviews">
			<div className={setClassNames(filterSortContainerClasses)}>
				<Filters availableFilters={availableFilters} />
				<Sort />
			</div>
			{errorOrReviews}
		</div>
	);
};

export default Widget;
